import React from "react";
import { Icon, Modal, Popup } from "semantic-ui-react";
import { useDispatch } from "react-redux";
import { Button, Grid } from "semantic-ui-react";
import { closeModal } from "../../app/common/modals/modalReducer";
import { sendEmail } from "../leads/leadActions";
import { renderToString } from "react-dom/server";
import BookingPDFPackage from "../booking/BookingPDF/BookingPDFPackage";
import { toast } from "react-toastify";
import { getPackageFlights } from "../quotes/quoteActions";
import { getClientConfirmation, getTraverllers, getClientPayment } from "../clients/clientActions";
import { Formik } from "formik";
import MyTextInput from "../../app/common/form/MyTextInput";
import MyTinyMCE from "../../app/common/form/MyTinyMCE";
import firebase from "../../app/config/firebase";
const firestore = firebase.firestore();
const TERMS_SIGN_TOKEN = "{{TERMS_SIGN_LINK}}";
const BOOKING_SIGN_TOKEN = "{{BOOKING_SIGN_LINK}}";
const BMS_BASE_URL = "https://bms.igotravel.co.za";

function ensureBookingSigningTokens(message) {
    const html = (message || "").toString();
    const additions = [];

    if (!html.includes(TERMS_SIGN_TOKEN)) {
        additions.push(`<p>${TERMS_SIGN_TOKEN}</p>`);
    }
    if (!html.includes(BOOKING_SIGN_TOKEN)) {
        additions.push(`<p>${BOOKING_SIGN_TOKEN}</p>`);
    }

    if (additions.length === 0) return html;
    return `${html}${html.trim() ? "<br />" : ""}${additions.join("")}`;
}

function stripBookingSigningTokens(message) {
    return (message || "")
        .toString()
        .replace(/<p>\s*\{\{TERMS_SIGN_LINK\}\}\s*<\/p>/g, "")
        .replace(/<p>\s*\{\{BOOKING_SIGN_LINK\}\}\s*<\/p>/g, "")
        .split(TERMS_SIGN_TOKEN)
        .join("")
        .split(BOOKING_SIGN_TOKEN)
        .join("")
        .replace(/(<br\s*\/?>\s*)+$/i, "");
}

function getBookingPackageId(clientBooking) {
    let packageId = clientBooking?.packageId || "";

    if (!packageId && clientBooking?.booked_package_id && clientBooking.booked_package_id.includes("|")) {
        packageId = clientBooking.booked_package_id.split("|")[0];
    }

    return packageId;
}

async function generateLaravelBookingPdfForEmail({ clientBooking, leadId, bookingId, currentUserProfile }) {
    const user = firebase.auth().currentUser;
    if (!user) {
        throw new Error("Please sign in to generate the booking PDF");
    }

    const token = await user.getIdToken();
    const payload = {
        booking_id: bookingId || clientBooking?.id || "",
        lead_id: leadId || clientBooking?.leadId || "",
        quote_id: clientBooking?.quoteId || "",
        package_id: getBookingPackageId(clientBooking),
        agent_uid: currentUserProfile?.id || "",
        agent_email: currentUserProfile?.email || "",
        agent_name: currentUserProfile?.displayName || "",
        request_signature: false,
        request_kin_details: false,
        request_traveler_details: false,
    };

    const response = await fetch(BMS_BASE_URL + "/api/booking/pdf/firebase", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: "Bearer " + token,
        },
        body: JSON.stringify(payload),
    });

    //A gateway/proxy error can return HTML, so don't let a parse failure hide the status code
    let data = null;
    try {
        data = await response.json();
    } catch (parseError) {
        console.log(parseError);
    }

    if (!response.ok) {
        throw new Error(data?.message || "Failed to generate booking PDF (error " + response.status + ")");
    }

    if (!data?.pdf_url) {
        throw new Error("No booking PDF URL returned");
    }

    return data.pdf_url;
}

export default function BookingEmailModal(props) {
    const dispatch = useDispatch();
    let client_email = props.client_email;
    let quote = props.quote;
    let client_booking = props.client_booking;
    let lead = props.lead;
    const forceLegacySend = props.forceLegacySend === true;
    const useLegacySigningBridge = props.useLegacySigningBridge === true;
    const useLaravelBookingPdf = props.useLaravelBookingPdf === true;
    const defaultUseNewFlow = !forceLegacySend && (props.defaultUseNewFlow === true || useLegacySigningBridge || useLaravelBookingPdf);

    // Defaults are overridden by `props.initialValues` when provided.
    let initialValues = {
        // Electronic signing remains the default; manual mode sends the normal email with the new Laravel PDF attached.
        signing_delivery: defaultUseNewFlow ? "electronic" : "legacy",
        use_new_flow: defaultUseNewFlow,
        include_booking_pdf: true,
        attachBookingTerms: false,
        request_signature: defaultUseNewFlow,
        request_booking_signature: defaultUseNewFlow,
        ...(props.initialValues || {}),
    };

    initialValues.signing_delivery = initialValues.signing_delivery || (defaultUseNewFlow ? "electronic" : "legacy");

    if (initialValues.signing_delivery === "electronic") {
        initialValues.use_new_flow = true;
        initialValues.include_booking_pdf = true;
        initialValues.attachBookingTerms = false;
        initialValues.request_signature = true;
        initialValues.request_booking_signature = true;
        initialValues.message = ensureBookingSigningTokens(initialValues.message);
    } else {
        initialValues.use_new_flow = false;
        initialValues.request_signature = false;
        initialValues.request_booking_signature = false;
        initialValues.message = stripBookingSigningTokens(initialValues.message);
        initialValues.include_booking_pdf = true;
    }

    return (
        <>
            <Modal size="large" open={true} onClose={() => dispatch(closeModal())} closeOnDimmerClick={false}>
                <Modal.Header>Send Email - {props.lead.client_name}</Modal.Header>
                <Modal.Content>
                    <Modal.Description>
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize={true}
                            validate={(values) => {
                                const errors = {};
                                if (!values.message) {
                                    errors.message = "Message is required";
                                }
                                if (!values.subject) {
                                    errors.subject = "Subject is required";
                                }
                                return errors;
                            }}
                            onSubmit={async (values, { setSubmitting }) => {
                                console.log(values);
                                const signingDelivery = values.signing_delivery || (values.use_new_flow === true ? "electronic" : "legacy");
                                const useNewFlow = signingDelivery === "electronic";
                                const useManualNewPdf = signingDelivery === "manual";

                                values.signing_delivery = signingDelivery;
                                values.use_new_flow = useNewFlow;

                                values.leadId = props.leadId;
                                values.quoteId = props.quoteId;
                                values.bookingId = props.bookingId;
                                values.to = props.client_email;
                                values.emailType = props.emailType;
                                values.agent_uid = props.currentUserProfile?.id;

                                // Persist flow choice so Functions can interpret the payload reliably.
                                values.email_flow = useNewFlow ? "booking_new_signing" : useManualNewPdf ? "booking_manual_new_pdf" : "booking_legacy";
                                values.use_signing_bridge = useNewFlow;
                                values.use_laravel_booking_pdf = useManualNewPdf || (useNewFlow && values.include_booking_pdf !== false);

                                //Consultant Details
                                values.fromEmail = props.currentUserProfile.email ? props.currentUserProfile.email : "noreply@igotravel.co.za";
                                values.fromName = props.currentUserProfile.displayName ? props.currentUserProfile.displayName : "IGO Travel";

                                //Mailtrap
                                if (props.currentUserProfile.mailtrap && props.currentUserProfile.mailtrap === true) {
                                    values.mailtrap = true;
                                }

                                const wantsSigning = useNewFlow && (values.request_signature === true || values.request_booking_signature === true);
                                // Booking email uses legacy callable; signing/link injection happens inside `sendEmailAPI` when enabled.
                                const functionName = "sendEmailAPI";
                                const firebaseApp = firebase.app();
                                const firebaseProjectId = firebaseApp?.options?.projectId;
                                const functionsRegion = "europe-west2";
                                const sendEmailCallable = firebaseApp.functions(functionsRegion).httpsCallable(functionName);

                                console.log("[Email] functionName:", functionName);
                                console.log("[Email] forceLegacySend:", forceLegacySend);
                                console.log("[Email] useNewFlow:", useNewFlow);
                                console.log("[Email] useManualNewPdf:", useManualNewPdf);
                                console.log("[Email] wantsSigning:", wantsSigning, {
                                    request_signature: values.request_signature,
                                    request_booking_signature: values.request_booking_signature,
                                });
                                console.log("[Email] firebaseProjectId:", firebaseProjectId);
                                console.log("[Email] functionsRegion:", functionsRegion);
                                console.log("[Email] leadId/quoteId/bookingId/emailType:", {
                                    leadId: values.leadId,
                                    quoteId: values.quoteId,
                                    bookingId: values.bookingId,
                                    emailType: values.emailType,
                                });

                                // Electronic signing sends signing links and can optionally attach the new Booking PDF.
                                if (useNewFlow) {
                                    values.include_booking_pdf = values.include_booking_pdf !== false;
                                    values.attachBookingTerms = false;
                                    values.request_signature = true;
                                    values.request_booking_signature = true;
                                    values.message = ensureBookingSigningTokens(values.message);
                                } else {
                                    values.request_signature = false;
                                    values.request_booking_signature = false;
                                    values.message = stripBookingSigningTokens(values.message);

                                    if (useManualNewPdf) {
                                        values.include_booking_pdf = true;
                                    }
                                }

                                const includeBookingPdf = values.include_booking_pdf !== false;

                                const sendEmailNow = async () => {
                                    let email_id = await sendEmail(values, props.currentUserProfile);
                                    console.log("[Email] email_id created:", email_id);

                                    await sendEmailCallable({ email_id: email_id })
                                        .then(async (result) => {
                                            console.log("[Email] callable result:", result);
                                            if (result.data === "Email sent OK") {
                                                toast.success("Email has been queued to send");
                                                // Avoid Formik setState-on-unmounted warning by clearing submitting before closing the modal.
                                                setSubmitting(false);
                                                dispatch(closeModal());
                                            } else {
                                                toast.error("Oops", "Error sending email - Type 2");
                                                setSubmitting(false);
                                            }

                                            console.log(result);
                                        })
                                        .catch((error) => {
                                            console.error("[Email] callable error:", error);
                                            toast.error(error?.message || "Error sending email");
                                            setSubmitting(false);
                                        });
                                };

                                if (!includeBookingPdf) {
                                    await sendEmailNow();
                                    return;
                                }

                                if (useNewFlow || useManualNewPdf) {
                                    try {
                                        values.pdf = await generateLaravelBookingPdfForEmail({
                                            clientBooking: client_booking,
                                            leadId: props.leadId,
                                            bookingId: props.bookingId,
                                            currentUserProfile: props.currentUserProfile,
                                        });
                                        await sendEmailNow();
                                    } catch (error) {
                                        console.log(error);
                                        toast.error(error?.message || "Failed to generate booking PDF");
                                        setSubmitting(false);
                                    }
                                    return;
                                }

                                //Generate a quote URL
                                let quoteNew = props.quote;
                                let quote_templates = props.quote_templates;

                                //Find Quote template
                                Object.keys(quote_templates).forEach(function (key) {
                                    if (quote_templates[key].id === quote.template_id) {
                                        quoteNew.templateUrl = quote_templates[key].fileUrl;
                                        quoteNew.template_name = quote_templates[key].template_name;
                                        quoteNew.visa = quote_templates[key].visa;
                                        quoteNew.destination_link = quote_templates[key].destination_link;
                                        quoteNew.wording = quote_templates[key].wording ? quote_templates[key].wording : "";
                                    }
                                });

                                // let flights = await getPackageFlights(client_booking.quoteId);

                                // if (flights.length > 0) {
                                //     flights.sort((a, b) => (a.departDateTime > b.departDateTime ? 1 : -1));
                                // }

                                let flightlist = [];

                                firestore
                                    .collection("quotes")
                                    .doc(client_booking.quoteId)
                                    .collection("packages")
                                    .onSnapshot((snapshot) => {
                                        snapshot.docs.forEach((doc) => {
                                            if (doc.exists) {
                                                firestore
                                                    .collection("quotes")
                                                    .doc(client_booking.quoteId)
                                                    .collection("packages")
                                                    .doc(doc.id)
                                                    .collection("flights")
                                                    .onSnapshot((snapshot2) => {
                                                        if (snapshot2.docs && snapshot2.docs.length > 0) {
                                                            snapshot2.docs.forEach((doc2) => {
                                                                let flight = doc2.data();
                                                                flightlist.push(flight);
                                                            });
                                                        }
                                                    });
                                            }
                                        });
                                    });

                                const someTimeoutAction = () => {
                                    return new Promise((resolve) => {
                                        setTimeout(function () {
                                            resolve(flightlist);
                                        }, 500);
                                    });
                                };

                                const flights = await someTimeoutAction();

                                if (flights.length > 0) {
                                    flights.sort((a, b) => (a.departDateTime > b.departDateTime ? 1 : -1));
                                }

                                let client_confirmation = {};

                                await getClientConfirmation(client_booking.id).then(
                                    (res) => {
                                        let list = [];
                                        res.forEach((doc) => {
                                            if (doc.exists) {
                                                let appObj = { ...doc.data(), ["id"]: `${doc.id}` };
                                                list.push(appObj);
                                            }
                                        });
                                        client_confirmation = list;
                                    },
                                    (err) => {
                                        console.log(err);
                                    }
                                );

                                let travellers = {};

                                await getTraverllers(client_booking.id).then(
                                    (res) => {
                                        let list = [];
                                        res.forEach((doc) => {
                                            if (doc.exists) {
                                                let appObj = { ...doc.data(), ["id"]: `${doc.id}` };
                                                list.push(appObj);
                                            }
                                        });
                                        travellers = list;
                                    },
                                    (err) => {
                                        console.log(err);
                                    }
                                );

                                let payments = {};

                                await getClientPayment(client_booking.id).then(
                                    (res) => {
                                        let list = [];
                                        res.forEach((doc) => {
                                            if (doc.exists) {
                                                let appObj = { ...doc.data(), ["id"]: `${doc.id}` };
                                                list.push(appObj);
                                            }
                                        });
                                        payments = list;
                                    },
                                    (err) => {
                                        console.log(err);
                                    }
                                );

                                let qtPackages = [];
                                let breakdown = [];

                                firestore
                                    .collection("quotes")
                                    .doc(client_booking.quoteId)
                                    .collection("packages")
                                    .doc(client_booking.packageId)
                                    .onSnapshot((snapshot2) => {
                                        if (snapshot2.exists) {
                                            qtPackages.flight_html = snapshot2.data().flight_html;

                                            const priceBreakdown = Array.isArray(snapshot2.data()?.price_breakdown)
                                                ? snapshot2.data().price_breakdown
                                                : [];

                                            for (let i = 0; i < priceBreakdown.length; i++) {
                                                if (priceBreakdown[i]?.value) {
                                                    breakdown.push(priceBreakdown[i].value);
                                                }
                                            }
                                        }
                                    });

                                const someTimeoutActionPayment = () => {
                                    return new Promise((resolve) => {
                                        setTimeout(function () {
                                            resolve(qtPackages);
                                        }, 500);
                                    });
                                };

                                const qtPackage = await someTimeoutActionPayment();

                                const pdfPackage = renderToString(
                                    <BookingPDFPackage
                                        qtPackage={qtPackage}
                                        breakdown={breakdown}
                                        client_booking={client_booking}
                                        quote={quoteNew}
                                        lead={lead}
                                        quote_templates={quote_templates}
                                        client_confirmation={client_confirmation}
                                        travellers={travellers}
                                        flights={flights}
                                        payments={payments}
                                    />
                                );

                                let html = pdfPackage;

                                await fetch("https://v2018.api2pdf.com//chrome/html", {
                                    method: "post",
                                    headers: {
                                        Accept: "application/json",
                                        "Content-Type": "application/json",
                                        Authorization: "104a3c14-8782-4ee5-885f-000b5a7fb8a1",
                                    },
                                    body: JSON.stringify({
                                        html: html,
                                        inlinePdf: true,
                                        fileName: "test.pdf",
                                        options: {
                                            marginBottom: 0,
                                            marginLeft: 0,
                                            marginRight: 0,
                                            marginTop: 0,
                                        },
                                    }),
                                })
                                    .then((res) => res.json())
                                    .then(async (res) => {
                                        if (res.success === true) {
                                            values.pdf = res.pdf;
                                            await sendEmailNow();
                                        } else {
                                            toast.error("Oops", "Error sending email - Type 1");
                                            setSubmitting(false);
                                        }
                                    });
                            }}
                        >
                            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
                                <form onSubmit={handleSubmit} className="ui form">
                                    <MyTextInput label="Subject*" name="subject" type="text" />
                                    {errors && errors.subject ? <div className="ui red basic label">Subject is Required</div> : null}

                                    <Grid verticalAlign="middle">
                                        <Grid.Row>
                                            <Grid.Column width={5}>
                                                <label>
                                                    <strong>To</strong>
                                                </label>
                                            </Grid.Column>

                                            <Grid.Column width={11}>{client_email}</Grid.Column>
                                        </Grid.Row>
                                    </Grid>

                                    <MyTextInput label="CC" name="cc" type="text" />

                                    <MyTextInput label="BCC" name="bcc" type="text" />

                                    <p>
                                        <em>*Note, for multiple emails seperate with a comma.</em>
                                    </p>

                                    <div style={{ marginBottom: 20, marginTop: 20 }}>
                                        <p>
                                            <strong>Email Flow</strong>
                                        </p>
                                        <label>
                                            <input
                                                type="radio"
                                                name="signing_delivery"
                                                value="electronic"
                                                className="ui radio checkbox"
                                                checked={values.signing_delivery === "electronic"}
                                                onChange={() => {
                                                    setFieldValue("signing_delivery", "electronic");
                                                    setFieldValue("use_new_flow", true);
                                                    setFieldValue("include_booking_pdf", true);
                                                    setFieldValue("attachBookingTerms", false);
                                                    setFieldValue("request_signature", true);
                                                    setFieldValue("request_booking_signature", true);
                                                    setFieldValue("message", ensureBookingSigningTokens(values.message));
                                                }}
                                            />{" "}
                                            &nbsp; Send signing links (recommended)
                                        </label>
                                        <p style={{ fontSize: 12, marginTop: 6 }}>
                                            Sends secure Terms and Booking signing links. The new Booking PDF can be attached below.
                                        </p>

                                        <label>
                                            <input
                                                type="radio"
                                                name="signing_delivery"
                                                value="manual"
                                                className="ui radio checkbox"
                                                checked={values.signing_delivery === "manual"}
                                                onChange={() => {
                                                    setFieldValue("signing_delivery", "manual");
                                                    setFieldValue("use_new_flow", false);
                                                    setFieldValue("include_booking_pdf", true);
                                                    setFieldValue("request_signature", false);
                                                    setFieldValue("request_booking_signature", false);
                                                    setFieldValue("message", stripBookingSigningTokens(values.message));
                                                }}
                                            />{" "}
                                            &nbsp; Manual signing email
                                        </label>
                                        <p style={{ fontSize: 12, marginTop: 6 }}>
                                            Sends the traditional email without signing links and attaches the new Booking PDF.
                                        </p>

                                        <label>
                                            <input
                                                type="radio"
                                                name="signing_delivery"
                                                value="legacy"
                                                className="ui radio checkbox"
                                                checked={values.signing_delivery === "legacy"}
                                                onChange={() => {
                                                    setFieldValue("signing_delivery", "legacy");
                                                    setFieldValue("use_new_flow", false);
                                                    setFieldValue("include_booking_pdf", true);
                                                    setFieldValue("request_signature", false);
                                                    setFieldValue("request_booking_signature", false);
                                                    setFieldValue("message", stripBookingSigningTokens(values.message));
                                                }}
                                            />{" "}
                                            &nbsp; Legacy PDF email fallback
                                        </label>
                                        <hr style={{ margin: "16px 0" }} />
                                    </div>

                                    {values.signing_delivery === "electronic" && (
                                        <div style={{ marginBottom: 20, marginTop: 20 }}>
                                            <p>
                                                <strong>Signature</strong>
                                            </p>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    name="request_signature"
                                                    value="true"
                                                    className="ui checkbox"
                                                    checked={values.signing_delivery === "electronic" || values.request_signature === true}
                                                    disabled={values.signing_delivery === "electronic"}
                                                    onChange={(value) => setFieldValue("request_signature", value.target.checked)}
                                                />{" "}
                                                &nbsp; Request electronic signature (Terms &amp; Conditions)
                                            </label>
                                            <p style={{ fontSize: 12, marginTop: 6 }}>
                                                The email will include a secure Terms signing link.
                                            </p>

                                            <label>
                                                <input
                                                    type="checkbox"
                                                    name="request_booking_signature"
                                                    value="true"
                                                    className="ui checkbox"
                                                    checked={values.signing_delivery === "electronic" || values.request_booking_signature === true}
                                                    disabled={values.signing_delivery === "electronic"}
                                                    onChange={(value) => setFieldValue("request_booking_signature", value.target.checked)}
                                                />{" "}
                                                &nbsp; Request electronic signature (Booking)
                                            </label>
                                            <p style={{ fontSize: 12, marginTop: 6 }}>
                                                The email will include a secure booking signing link.
                                            </p>

                                            <hr style={{ margin: "16px 0" }} />
                                        </div>
                                    )}

                                    <div style={{ marginBottom: 20, marginTop: 20 }}>
                                        <p>
                                            <strong>Attach Documents</strong>
                                        </p>

                                        {values.signing_delivery === "electronic" && (
                                            <>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        name="include_booking_pdf"
                                                        value="true"
                                                        className="ui checkbox"
                                                        checked={values.include_booking_pdf !== false}
                                                        onChange={(value) => setFieldValue("include_booking_pdf", value.target.checked)}
                                                    />{" "}
                                                    &nbsp; Include Booking PDF (new)
                                                </label>
                                                <p style={{ fontSize: 12, marginTop: 6 }}>
                                                    Attach the Booking PDF for reference. The client must still use the signing buttons in the email to sign.
                                                </p>
                                            </>
                                        )}

                                        {values.signing_delivery === "manual" && (
                                            <>
                                                <label>
                                                    <input type="checkbox" className="ui checkbox" checked={true} disabled={true} readOnly />{" "}
                                                    &nbsp; Include Booking PDF (new)
                                                </label>
                                                <p style={{ fontSize: 12, marginTop: 6 }}>
                                                    This PDF is generated by the Laravel/BMS booking PDF service before the email is sent.
                                                </p>
                                            </>
                                        )}

                                        {values.signing_delivery === "legacy" && (
                                            <>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        name="include_booking_pdf"
                                                        value="true"
                                                        className="ui checkbox"
                                                        checked={values.include_booking_pdf !== false}
                                                        onChange={(value) => setFieldValue("include_booking_pdf", value.target.checked)}
                                                    />{" "}
                                                    &nbsp; Include Booking PDF (legacy)
                                                </label>
                                                <br />
                                                <br />
                                            </>
                                        )}

                                        <label>
                                            <input
                                                type="checkbox"
                                                name="attachBankTravel"
                                                value="true"
                                                className="ui checkbox"
                                                onChange={(value) => setFieldValue("attachBankTravel", value.target.checked)}
                                            />{" "}
                                            &nbsp; Bank details (Travel)
                                        </label>
                                        <br />
                                        <br />

                                        <label>
                                            <input
                                                type="checkbox"
                                                name="attachBankCorporate"
                                                value="true"
                                                className="ui checkbox"
                                                onChange={(value) => setFieldValue("attachBankCorporate", value.target.checked)}
                                            />{" "}
                                            &nbsp; Bank details (Corporate)
                                        </label>
                                        <br />
                                        <br />

                                        <label>
                                            <input type="checkbox" name="attachCC" value="true" className="ui checkbox" onChange={(value) => setFieldValue("attachCC", value.target.checked)} /> &nbsp;
                                            Credit Card Form
                                        </label>
                                        <br />
                                        <br />

                                        <label>
                                            <input
                                                type="checkbox"
                                                name="attachBryteBrochure"
                                                value="true"
                                                className="ui checkbox"
                                                onChange={(value) => setFieldValue("attachBryteBrochure", value.target.checked)}
                                            />{" "}
                                            &nbsp; Bryte Brochure
                                        </label>
                                        <br />
                                        <br />

                                        <label>
                                            <input
                                                type="checkbox"
                                                name="attachBrytePolicy"
                                                value="true"
                                                className="ui checkbox"
                                                onChange={(value) => setFieldValue("attachBrytePolicy", value.target.checked)}
                                            />{" "}
                                            &nbsp; Bryte Policy Wording
                                        </label>
                                        <br />
                                        <br />

                                        <label>
                                            <input
                                                type="checkbox"
                                                name="attachAsataCertificate"
                                                value="true"
                                                className="ui checkbox"
                                                onChange={(value) => setFieldValue("attachAsataCertificate", value.target.checked)}
                                            />{" "}
                                            &nbsp; ASATA Certificate
                                        </label>
                                        <br />
                                        <br />

                                        <label>
                                            <input
                                                type="checkbox"
                                                name="attachIataCertificate"
                                                value="true"
                                                className="ui checkbox"
                                                onChange={(value) => setFieldValue("attachIataCertificate", value.target.checked)}
                                            />{" "}
                                            &nbsp; IATA Certificate
                                        </label>
                                        <br />
                                        <br />

                                        {values.signing_delivery !== "electronic" && (
                                            <>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        name="attachBookingTerms"
                                                        value="true"
                                                        className="ui checkbox"
                                                        onChange={(value) => setFieldValue("attachBookingTerms", value.target.checked)}
                                                    />{" "}
                                                    &nbsp; Terms & Conditions
                                                </label>
                                                <br />
                                                <br />
                                            </>
                                        )}
                                    </div>

                                    <MyTinyMCE
                                        key={values.signing_delivery === "electronic" ? "booking-email-new-flow" : "booking-email-legacy-flow"}
                                        label="Email Message*"
                                        name="message"
                                        type="text"
                                        helperText={values.signing_delivery === "electronic" ? (
                                            <>
                                                Use placeholders instead of pasting tracked URLs. Links are injected/removed at send time.
                                                <br />
                                                Available tokens: <code>{TERMS_SIGN_TOKEN}</code>, <code>{BOOKING_SIGN_TOKEN}</code>.{" "}
                                                <Popup
                                                    position="top left"
                                                    trigger={<Icon name="info circle" style={{ marginLeft: 6, cursor: "pointer" }} />}
                                                    content={
                                                        <div style={{ maxWidth: 340 }}>
                                                            <strong>How it works</strong>
                                                            <div style={{ marginTop: 6 }}>
                                                                1) Copy/paste the token <code>{TERMS_SIGN_TOKEN}</code> or{" "}
                                                                <code>{BOOKING_SIGN_TOKEN}</code> exactly (including the double curly braces).
                                                            </div>
                                                            <div style={{ marginTop: 6 }}>
                                                                2) New flow locks both signing options on. If either token is missing, the modal adds it before
                                                                sending.
                                                            </div>
                                                            <div style={{ marginTop: 6 }}>
                                                                3) The email gets a secure signing URL from the server and injects the clickable link where the
                                                                token is.
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                                <br />
                                                {/* <em>TODO: once client confirms, remove legacy hardcoded Terms links from templates and use tokens only.</em> */}
                                            </>
                                        ) : null}
                                        helperTextStyle={values.signing_delivery === "electronic" ? { color: "#b00020" } : undefined}
                                        tokenButtons={
                                            values.signing_delivery === "electronic"
                                                ? [
                                                    { name: "insert_terms_sign", text: "Insert Terms Sign Link", token: TERMS_SIGN_TOKEN },
                                                    { name: "insert_booking_sign", text: "Insert Booking Sign Link", token: BOOKING_SIGN_TOKEN },
                                                ]
                                                : []
                                        }
                                        onEditorChange={(value) => setFieldValue("message", value)}
                                    />

                                    <div style={{ marginTop: 20 }}>
                                        {client_email && client_email.length > 0 && (
                                            <Button color="orange" disabled={isSubmitting} loading={isSubmitting}>
                                                Send Email
                                            </Button>
                                        )}

                                        <Button disabled={isSubmitting} onClick={() => dispatch(closeModal())}>
                                            Cancel
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </Formik>
                    </Modal.Description>
                </Modal.Content>
            </Modal>
        </>
    );
}
